import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { FreightStatus } from '@/types/freight'

export interface ReportPeriod {
  startDate?: string
  endDate?: string
}

export interface IncomeStatement {
  period: { startDate: string | null; endDate: string | null }
  inflows: Array<{ categoryId: string; categoryName: string; total: string; count: number }>
  outflows: Array<{ categoryId: string; categoryName: string; total: string; count: number }>
  totals: { inflow: string; outflow: string; net: string }
}

export interface FreightProfitabilityItem {
  freightId: string
  code: number
  status: FreightStatus
  createdAt: string
  company: { id: string; tradeName: string }
  driver: { id: string; name: string } | null
  vehicle: { id: string; plate: string } | null
  revenue: string
  driverPay: string
  margin: string
  marginPercent: string
}

export interface DriverProfitabilityItem {
  driverId: string
  driverName: string
  freightCount: number
  revenue: string
  driverPay: string
  margin: string
}

export interface VehicleProfitabilityItem {
  vehicleId: string
  plate: string
  freightCount: number
  revenue: string
  driverPay: string
  expensesTotal: string
  expensesCount: number
  netResult: string
}

export function useIncomeStatement(period: ReportPeriod) {
  return useQuery({
    queryKey: ['reports', 'income-statement', period],
    queryFn: () =>
      api
        .get<IncomeStatement>('/reports/income-statement', { params: period })
        .then((r) => r.data),
  })
}

export function useFreightProfitability(period: ReportPeriod) {
  return useQuery({
    queryKey: ['reports', 'freight-profitability', period],
    queryFn: () =>
      api
        .get<{ items: FreightProfitabilityItem[] }>('/reports/freight-profitability', {
          params: period,
        })
        .then((r) => r.data),
  })
}

export function useDriverProfitability(period: ReportPeriod) {
  return useQuery({
    queryKey: ['reports', 'driver-profitability', period],
    queryFn: () =>
      api
        .get<{ items: DriverProfitabilityItem[] }>('/reports/driver-profitability', {
          params: period,
        })
        .then((r) => r.data),
  })
}

export function useVehicleProfitability(period: ReportPeriod) {
  return useQuery({
    queryKey: ['reports', 'vehicle-profitability', period],
    queryFn: () =>
      api
        .get<{ items: VehicleProfitabilityItem[] }>('/reports/vehicle-profitability', {
          params: period,
        })
        .then((r) => r.data),
  })
}
